// Dashboard: what needs a super-admin today, how tenants are doing, and how
// the six add-ons are landing.
//
//   • KPI tiles     tenants by account status (links to the filtered list)
//                   and platform activity (super-admins excluded)
//   • Needs attention  useAttentionQueue(): the server's queue, tenants quiet
//                   for 14+ days, and every new inquiry (owner, November 3: the
//                   separate Inquiries card went into it)
//   • Add-on adoption  "on" vs "used in 30 days" per add-on
//   • Tenant activity  per-tenant health, last login / change, views
//                   (GET /platform/usage/overview; replaces Pulse's churn radar)

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/api'
import { ACCOUNT_STATUSES, ADDON_KEYS, type AttentionItem, type TenantHealth, type UsageOverview } from '@/api/types'
import { PageHeader } from '@/components/shell/PageHeader'
import {
  Badge,
  BarList,
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorState,
  FilterChips,
  RelativeTime,
  SkeletonRows,
  Sparkline,
  StatTile,
  type BadgeTone,
  type Column,
} from '@/components/ui'
import { ADDONS } from '@/lib/addons'
import { PLATFORM_DOMAIN } from '@/lib/tenant'
import { formatCount, formatShortDate } from '@/lib/format'
import { useDocumentTitle } from '@/lib/hooks'
import { useQuery } from '@/lib/query'
import { accountStatusLabel } from '@/lib/status'
import { useAttentionQueue, type QueueItem } from '@/lib/useAttentionQueue'
import { INACTIVE_DAYS } from '@/api/dashboard'

// ─── Attention vocabulary ───────────────────────────────────────────────────

const KIND_LABEL: Record<QueueItem['kind'], string> = {
  new_inquiry: 'New inquiry',
  trial_ending: 'Trial ending',
  trial_ended: 'Trial ended',
  past_due: 'Past due',
  suspended: 'Suspended',
  domain_pending: 'Domain pending',
  domain_error: 'Domain error',
  no_admin: 'No admin',
  no_sport: 'No sport',
  inactive: `Inactive ${INACTIVE_DAYS}+ days`,
}

type Group = 'all' | 'inquiries' | 'billing' | 'domains' | 'activity' | 'setup'

const KIND_GROUP: Record<QueueItem['kind'], Exclude<Group, 'all'>> = {
  new_inquiry: 'inquiries',
  trial_ending: 'billing',
  trial_ended: 'billing',
  past_due: 'billing',
  suspended: 'billing',
  domain_pending: 'domains',
  domain_error: 'domains',
  inactive: 'activity',
  no_admin: 'setup',
  no_sport: 'setup',
}

const GROUP_LABEL: Record<Group, string> = { all: 'All', inquiries: 'Inquiries', billing: 'Billing', domains: 'Domains', activity: 'Activity', setup: 'Setup' }
const SEVERITY_TONE: Record<AttentionItem['severity'], BadgeTone> = { high: 'bad', medium: 'warn', low: 'neutral' }
const SEVERITY_LABEL: Record<AttentionItem['severity'], string> = { high: 'Urgent', medium: 'Soon', low: 'FYI' }

// ─── Health vocabulary (usage overview) ─────────────────────────────────────

const HEALTH_TONE: Record<TenantHealth, BadgeTone> = { dormant: 'bad', quiet: 'warn', active: 'good', new: 'accent' }
const HEALTH_LABEL: Record<TenantHealth, string> = { dormant: 'Dormant', quiet: 'Quiet', active: 'Active', new: 'New' }
const HEALTH_RANK: Record<TenantHealth, number> = { dormant: 0, quiet: 1, active: 2, new: 3 }

type OverviewRow = UsageOverview['leagues'][number]

export default function DashboardPage() {
  useDocumentTitle('Dashboard')
  const queue = useAttentionQueue()
  const dash = queue.dashboard

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader title="Dashboard" description="What needs you today, how tenants are doing, and how the add-ons are landing." />

      {queue.status === 'error' && !dash ? (
        <Card>
          <ErrorState error={queue.error} title="Couldn't load the dashboard" onRetry={queue.refetch} />
        </Card>
      ) : (
        <>
          <section aria-labelledby="kpi-status" className="grid gap-2">
            <h2 id="kpi-status" className="sr-only">
              Tenants by status
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {ACCOUNT_STATUSES.map((s) => (
                <StatTile
                  key={s}
                  to={`/tenants?status=${s}`}
                  label={s === 'trialing' ? 'In trial' : accountStatusLabel(s)}
                  value={dash ? formatCount(dash.tenants_by_status[s] ?? 0) : null}
                  tone={s === 'past_due' && dash?.tenants_by_status.past_due ? 'warn' : 'default'}
                />
              ))}
            </div>
          </section>

          <ActivityTiles queue={queue} />

          <div className="grid items-start gap-4 lg:grid-cols-[1.4fr_1fr]">
            <AttentionCard queue={queue} />
            <div className="grid min-w-0 gap-4">
              <AdoptionCard queue={queue} />
            </div>
          </div>

          <TenantActivityCard />
        </>
      )}
    </div>
  )
}

// ─── Activity tiles ─────────────────────────────────────────────────────────

function ActivityTiles({ queue }: { queue: ReturnType<typeof useAttentionQueue> }) {
  const a = queue.dashboard?.activity
  const n = (v: number | undefined) => (v === undefined ? null : formatCount(v))
  return (
    <section aria-labelledby="kpi-activity" className="grid gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h2 id="kpi-activity" className="font-sans text-2xs font-bold uppercase tracking-eyebrow text-muted">
          Platform activity
        </h2>
        <p className="text-2xs text-subtle">League admins and fans only; super-admin activity is excluded.</p>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatTile label="Active tenants · 7d" value={n(a?.active_tenants_7d)} hint="had league-admin activity" />
        <StatTile label="Tenant logins · 7d" value={n(a?.tenant_logins_7d)} />
        <StatTile label="Content changes · 7d" value={n(a?.content_changes_7d)} />
        <StatTile label="Fan page views · 7d" value={n(a?.fan_page_views_7d)} />
        <StatTile label="Fan page views · 14d" value={n(a?.fan_page_views_14d)} className="col-span-2 sm:col-span-1" />
      </div>
    </section>
  )
}

// ─── Needs attention ────────────────────────────────────────────────────────

function AttentionCard({ queue }: { queue: ReturnType<typeof useAttentionQueue> }) {
  const [group, setGroup] = useState<Group>('all')
  const counts = useMemo(() => {
    const c: Record<Group, number> = { all: queue.items.length, inquiries: 0, billing: 0, domains: 0, activity: 0, setup: 0 }
    for (const i of queue.items) c[KIND_GROUP[i.kind]] += 1
    return c
  }, [queue.items])
  const shown = group === 'all' ? queue.items : queue.items.filter((i) => KIND_GROUP[i.kind] === group)

  return (
    <Card flush className="overflow-hidden">
      <div className="grid gap-3 px-4 pt-4 sm:px-5">
        <CardHeader
          className="mb-0"
          title="Needs attention"
          description={`New inquiries, trials ending within 7 days or already ended, past due, broken domains, and tenants quiet for ${INACTIVE_DAYS}+ days.`}
        />
        {queue.status === 'success' && queue.items.length ? (
          <FilterChips
            label="Filter needs-attention items"
            value={group}
            onChange={setGroup}
            options={(Object.keys(GROUP_LABEL) as Group[]).map((g) => ({ value: g, label: GROUP_LABEL[g], count: counts[g] }))}
          />
        ) : null}
      </div>
      <div className="mt-3">
        {queue.status === 'loading' ? (
          <SkeletonRows rows={4} label="Loading needs attention" />
        ) : queue.status === 'error' ? (
          <ErrorState error={queue.error} onRetry={queue.refetch} />
        ) : queue.items.length === 0 ? (
          <EmptyState title="All clear" description="No new inquiries, and no tenant needs action right now." />
        ) : shown.length === 0 ? (
          <EmptyState title={`Nothing in ${GROUP_LABEL[group]}`} description="Pick another filter to see the rest." />
        ) : (
          <ul aria-label="Needs attention" className="divide-y divide-line border-t border-line">
            {shown.map((item) => (
              <AttentionRow key={'tenant' in item ? `${item.kind}-${item.tenant.id}` : `inquiry-${item.inquiry.id}`} item={item} />
            ))}
          </ul>
        )}
      </div>
    </Card>
  )
}

function AttentionRow({ item }: { item: QueueItem }) {
  const tenant = 'tenant' in item
  return (
    <li>
      <Link
        to={tenant ? `/tenants/${item.tenant.id}/${item.tab}` : `/growth/inquiries/${item.inquiry.id}`}
        className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1 px-4 py-3 transition-colors hover:bg-hover/50 sm:px-5"
      >
        <span className="grid min-w-0 gap-0.5">
          <span className="truncate font-semibold text-ink">{tenant ? item.tenant.name : item.inquiry.name}</span>
          <span className={tenant ? 'text-xs text-muted' : 'line-clamp-2 text-xs text-muted'}>
            <span className="font-semibold text-ink/80">{tenant ? KIND_LABEL[item.kind] : `${KIND_LABEL[item.kind]}: ${item.inquiry.topic}`}</span>
            {' · '}
            {item.message}
            {item.at ? <span className="text-subtle"> · {formatShortDate(item.at)}</span> : null}
          </span>
        </span>
        <Badge tone={SEVERITY_TONE[item.severity]}>{SEVERITY_LABEL[item.severity]}</Badge>
      </Link>
    </li>
  )
}

// ─── Add-on adoption ────────────────────────────────────────────────────────

function AdoptionCard({ queue }: { queue: ReturnType<typeof useAttentionQueue> }) {
  const dash = queue.dashboard
  const total = dash ? Object.values(dash.tenants_by_status).reduce((a, b) => a + b, 0) : 0
  const byKey = new Map((dash?.addon_adoption ?? []).map((a) => [a.key, a]))
  const items = ADDON_KEYS.map((k) => ({
    label: ADDONS[k].label,
    value: byKey.get(k)?.enabled ?? 0,
    inner: byKey.get(k)?.used_30d ?? 0,
  }))
  return (
    <Card>
      <CardHeader
        title="Add-on adoption"
        description={
          dash ? `Tenants with each add-on on (of ${total}), and how many used it in the last 30 days.` : undefined
        }
      />
      {queue.status === 'loading' ? (
        <SkeletonRows rows={3} label="Loading add-on adoption" className="-mx-4 sm:-mx-5" />
      ) : queue.status === 'error' ? (
        <ErrorState error={queue.error} onRetry={queue.refetch} className="py-6" />
      ) : (
        <>
          <p className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-2xs text-muted">
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden className="h-2 w-3 rounded-full bg-accent" /> Used in 30 days
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden className="h-2 w-3 rounded-full bg-accent/45" /> On
            </span>
          </p>
          <BarList label="Add-on adoption" items={items} max={Math.max(1, total)} valueLabel="Tenants with it on" innerLabel="Used in the last 30 days" innerJoiner="used of" />
        </>
      )}
    </Card>
  )
}

// ─── Tenant activity (was Pulse's churn radar) ──────────────────────────────

function sortRows(rows: OverviewRow[]): OverviewRow[] {
  const age = (iso: string | null) => (iso ? new Date(iso).getTime() : 0)
  return [...rows].sort((a, b) => HEALTH_RANK[a.health] - HEALTH_RANK[b.health] || age(a.last_activity_at) - age(b.last_activity_at))
}

const ACTIVITY_COLUMNS: Column<OverviewRow>[] = [
  {
    id: 'tenant',
    header: 'Tenant',
    primary: true,
    cell: (r) => (
      <span className="grid min-w-0 gap-0.5">
        <span className="break-words font-semibold">{r.name}</span>
        <span className="truncate font-mono text-xs font-medium text-muted">{r.subdomain ? `${r.subdomain}.${PLATFORM_DOMAIN}` : 'no subdomain'}</span>
      </span>
    ),
    className: 'min-w-[13rem] max-w-[16rem]',
  },
  { id: 'health', header: 'Health', cell: (r) => <Badge tone={HEALTH_TONE[r.health]}>{HEALTH_LABEL[r.health]}</Badge> },
  { id: 'login', header: 'Last login', cell: (r) => <RelativeTime value={r.last_login_at} staleAfterDays={14} /> },
  { id: 'change', header: 'Last change', cell: (r) => <RelativeTime value={r.last_change_at} staleAfterDays={14} /> },
  { id: 'admins', header: 'Admins', align: 'right', cell: (r) => <span className="tabular">{r.admin_count}</span> },
  { id: 'changes', header: 'Changes 7d', align: 'right', cell: (r) => <span className={r.changes_7d ? 'tabular' : 'tabular text-subtle'}>{r.changes_7d}</span> },
  {
    id: 'views',
    header: 'Views 14d',
    mobileLabel: 'Page views 14d',
    cell: (r) => (
      <span className="inline-flex items-center gap-2">
        <Sparkline data={r.page_views_sparkline} />
        <span className="text-xs text-muted tabular">{formatCount(r.page_views_sparkline.reduce((a, b) => a + b, 0))}</span>
      </span>
    ),
  },
]

function TenantActivityCard() {
  const { data, error, status, refetch } = useQuery(['dashboard', 'overview'], (signal) => api.dashboard.usageOverview(signal))
  const rows = useMemo(() => sortRows(data?.leagues ?? []), [data])
  const dormant = rows.filter((r) => r.health === 'dormant').length
  return (
    <Card flush className="overflow-hidden">
      <div className="px-4 pt-4 sm:px-5">
        <CardHeader
          title="Tenant activity"
          description={
            data
              ? dormant
                ? `${dormant} dormant ${dormant === 1 ? 'tenant' : 'tenants'}, worst first. Health: active ≤ 7 days, quiet ≤ 30, dormant beyond.`
                : 'Every tenant, worst health first. Health: active ≤ 7 days, quiet ≤ 30, dormant beyond.'
              : undefined
          }
        />
      </div>
      {status === 'loading' ? (
        <SkeletonRows rows={5} label="Loading tenant activity" />
      ) : status === 'error' && !data ? (
        <ErrorState error={error} title="Couldn't load tenant activity" onRetry={refetch} />
      ) : rows.length === 0 ? (
        <EmptyState title="No tenants yet" description="Activity shows up here once the first league exists." />
      ) : (
        <div className="border-t border-line">
          <DataTable caption="Tenant activity" rows={rows} columns={ACTIVITY_COLUMNS} rowKey={(r) => r.id} rowHref={(r) => `/tenants/${r.id}/activity`} />
        </div>
      )}
    </Card>
  )
}
